import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { FButton } from "@/components/forkd/FButton";
import { FInput } from "@/components/forkd/FInput";
import { onboarding } from "@/lib/onboarding";

type ProfileRow = {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
};

type CityRow = { id: string; name: string };

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const finish = () => {
    onboarding.markComplete();
    try {
      localStorage.setItem("forkd_post_onboarding_redirect", "1");
    } catch {}
    nav("/explore", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[var(--colour-background)] px-5 pt-12 pb-10 flex flex-col">
      <div className="flex items-center gap-1.5 mb-8">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className="flex-1 h-1 rounded-full"
            style={{
              backgroundColor:
                s <= step ? "var(--colour-accent)" : "var(--colour-border)",
            }}
          />
        ))}
      </div>
      {step === 1 && (
        <StepCity onNext={() => setStep(2)} onSkip={() => setStep(2)} />
      )}
      {step === 2 && (
        <StepLocation onNext={() => setStep(3)} onSkip={() => setStep(3)} />
      )}
      {step === 3 && <StepFriends onDone={finish} onSkip={finish} />}
    </div>
  );
}

function StepCity({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) {
  const { user } = useAuth();
  const [cities, setCities] = useState<CityRow[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: cityRows }, profileRes] = await Promise.all([
        supabase
          .from("cities")
          .select("id, name")
          .eq("is_active", true)
          .order("name"),
        user
          ? supabase
              .from("profiles")
              .select("home_city_id")
              .eq("id", user.id)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      setCities((cityRows ?? []) as CityRow[]);
      const existing = (profileRes as any)?.data?.home_city_id ?? null;
      if (existing) setSelected(existing);
      setLoading(false);
    })();
  }, [user]);

  const save = async () => {
    if (!selected || !user) {
      onNext();
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ home_city_id: selected })
      .eq("id", user.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    onNext();
  };

  return (
    <div className="flex-1 flex flex-col">
      <h2 className="text-[32px] font-extrabold leading-tight mb-2">
        Where do you eat?
      </h2>
      <p className="text-[15px] text-[var(--colour-text-secondary)] mb-6">
        We'll show you what's popular in your city.
      </p>

      {loading ? (
        <ul className="space-y-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <li
              key={i}
              className="h-14 rounded-[var(--radius-md)] animate-pulse"
              style={{ backgroundColor: "var(--colour-surface-raised)" }}
            />
          ))}
        </ul>
      ) : (
        <ul className="space-y-2.5">
          {cities.map((c, i) => {
            const active = c.id === selected;
            return (
              <li
                key={c.id}
                className="opacity-0"
                style={{
                  animation: `fkd-fade-up 320ms ease-out ${i * 60}ms forwards`,
                }}
              >
                <button
                  onClick={() => setSelected(c.id)}
                  className="w-full text-left flex items-center justify-between min-h-[56px] px-4 rounded-[var(--radius-md)] border transition-colors"
                  style={{
                    borderColor: active
                      ? "var(--colour-accent)"
                      : "var(--colour-border)",
                    backgroundColor: active
                      ? "color-mix(in oklab, var(--colour-accent) 8%, var(--colour-surface))"
                      : "var(--colour-surface)",
                  }}
                >
                  <span className="text-[16px] font-semibold">{c.name}</span>
                  {active && (
                    <Check size={20} className="text-[var(--colour-accent)]" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-auto pt-8 space-y-3">
        <FButton fullWidth onClick={save} disabled={!selected || saving}>
          {saving ? "Saving…" : "Continue"}
        </FButton>
        <button
          onClick={onSkip}
          className="block mx-auto text-[14px] text-[var(--colour-text-secondary)] min-h-[44px]"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

function StepLocation({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) {
  const request = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Geolocation not supported on this device");
      onNext();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => onNext(),
      () => onNext(),
      { enableHighAccuracy: false, timeout: 10000 },
    );
  };

  return (
    <div className="flex-1 flex flex-col items-center text-center pt-10">
      <div className="relative w-40 h-40 mb-6 flex items-center justify-center">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              width: 40,
              height: 40,
              border: "2px solid var(--colour-accent)",
              opacity: 0,
              animation: `fkd-pulse-ring 2.2s ease-out ${i * 0.7}s infinite`,
            }}
          />
        ))}
        <span
          className="relative w-3 h-3 rounded-full"
          style={{ backgroundColor: "var(--colour-accent)" }}
        />
      </div>
      <h2 className="text-[28px] font-bold leading-tight mb-3">
        Find food near you
      </h2>
      <p className="text-[15px] text-[var(--colour-text-secondary)] max-w-[320px]">
        Forkd uses your location only when you tap to find nearby restaurants. We
        never track you in the background.
      </p>

      <div className="mt-auto pt-10 w-full space-y-3">
        <FButton fullWidth onClick={request}>
          Allow Location Access
        </FButton>
        <button
          onClick={onSkip}
          className="block mx-auto text-[14px] text-[var(--colour-text-secondary)] min-h-[44px]"
        >
          Not now
        </button>
      </div>
    </div>
  );
}

function StepFriends({ onDone, onSkip }: { onDone: () => void; onSkip: () => void }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ProfileRow[]>([]);
  const [following, setFollowing] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handle = setTimeout(async () => {
      const q = query.trim().toLowerCase();
      if (!q) {
        setResults([]);
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url")
        .ilike("username", `%${q}%`)
        .neq("id", user?.id ?? "00000000-0000-0000-0000-000000000000")
        .limit(20);
      const rows = (data ?? []) as ProfileRow[];
      setResults(rows);

      // Seed the following set with any existing follows so the row shows the
      // correct state instead of letting the user trigger a duplicate insert.
      if (user && rows.length > 0) {
        const { data: existing } = await supabase
          .from("follows")
          .select("following_id")
          .eq("follower_id", user.id)
          .in(
            "following_id",
            rows.map((r) => r.id),
          );
        if (existing && existing.length > 0) {
          setFollowing((s) => {
            const next = new Set(s);
            for (const row of existing as { following_id: string }[]) {
              next.add(row.following_id);
            }
            return next;
          });
        }
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query, user?.id]);

  const follow = async (id: string) => {
    if (!user) return;
    // Optimistically mark as followed so duplicate taps don't re-insert.
    setFollowing((s) => new Set(s).add(id));
    const { error } = await supabase
      .from("follows")
      .upsert(
        { follower_id: user.id, following_id: id },
        { onConflict: "follower_id,following_id", ignoreDuplicates: true },
      );
    if (error) {
      // Duplicate-key races are fine — the user is already following.
      const code = (error as any).code;
      if (code === "23505") return;
      toast.error(error.message);
      // Roll back the optimistic update only on a real failure.
      setFollowing((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  const invite = async () => {
    const text = "I'm on Forkd, join me! https://forkd.app";
    const navAny = navigator as any;
    if (navAny.share) {
      try {
        await navAny.share({ text, title: "Forkd" });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Invite link copied");
    } catch {}
  };

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex items-start justify-between mb-2">
        <h2 className="text-[28px] font-bold leading-tight">Find people to follow</h2>
        <button
          onClick={onSkip}
          className="text-[14px] text-[var(--colour-text-secondary)] min-h-[44px] px-1 underline-offset-2 hover:underline"
        >
          Skip for now
        </button>
      </div>
      <p className="text-[15px] text-[var(--colour-text-secondary)] mb-5">
        Search for friends already on Forkd.
      </p>

      <div className="relative mb-4">
        <Search
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--colour-text-secondary)]"
        />
        <FInput
          name="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search usernames"
          className="pl-9"
        />
      </div>

      <ul className="space-y-2.5">
        {results.map((p) => {
          const isFollowing = following.has(p.id);
          return (
            <li
              key={p.id}
              onClick={() => nav(`/u/${p.username}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  nav(`/u/${p.username}`);
                }
              }}
              className="flex items-center gap-3 p-3 bg-[var(--colour-surface)] rounded-[var(--radius-md)] border border-[var(--colour-border)] cursor-pointer hover:bg-[var(--colour-surface-raised)] transition-colors"
            >
              <Avatar url={p.avatar_url} name={p.display_name} size={44} />
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold truncate">
                  {p.display_name}
                </div>
                <div className="text-[12px] text-[var(--colour-text-secondary)] truncate">
                  @{p.username}
                </div>
              </div>
              <FButton
                variant={isFollowing ? "outline" : "primary"}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isFollowing) follow(p.id);
                }}
                disabled={isFollowing}
                className="min-h-[36px] px-3 text-[13px]"
              >
                {isFollowing ? "Following" : "Follow"}
              </FButton>
            </li>
          );
        })}
        {query.trim() && results.length === 0 && (
          <li className="text-center py-6">
            <p className="text-[14px] text-[var(--colour-text-secondary)] mb-3">
              No one here yet — invite them!
            </p>
            <FButton variant="outline" onClick={invite}>
              <Share2 size={16} className="mr-2 inline" />
              Share invite
            </FButton>
          </li>
        )}
      </ul>

      <div className="mt-auto pt-8">
        <FButton fullWidth onClick={onDone}>
          Start exploring
        </FButton>
      </div>
    </div>
  );
}

function Avatar({ url, name, size }: { url: string | null; name: string; size: number }) {
  return url ? (
    <img
      src={url}
      alt={name}
      width={size}
      height={size}
      className="rounded-[var(--radius-full)] object-cover"
      style={{ width: size, height: size }}
    />
  ) : (
    <div
      className="rounded-[var(--radius-full)] bg-[var(--colour-surface-raised)] flex items-center justify-center text-[var(--colour-text-secondary)] font-semibold"
      style={{ width: size, height: size, fontSize: size / 2.6 }}
    >
      {name.slice(0, 1).toUpperCase()}
    </div>
  );
}
